import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useRef, useState, } from "react";
import { useApolloClient } from "@apollo/client";
import LoginPromptModal from "components/LoginPromptModal";
import ModalLoginContainer from "components/ModalLoginContainer";
import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";
import TurnstileWrapper from "components/TurnstileWrapper";
import { TURNSTILE_KEY } from "helpers/constants";
import { fetch as router } from "helpers/router";
import request from "utils/request";
import { csrf, captcha } from "utils/requestHeaders";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import loginSchema, { signupSchema } from "components/CommentModalAuthenticate/validation";
import { LoginForm, SignupForm } from "components/CommentModalAuthenticate";
import { REACTION_ICONS } from "../ReactionIconStack/ReactionIconStack";
import "./_style.scss";
const TypedTurnstileWrapper = TurnstileWrapper;
const AuthForm = ({ step, errorMessage, updateId, selectedReaction, captchaToken, isPosting, isRefreshing, onSwitch, onSuccess, onError, }) => {
    const { translate: t } = useLanguageProvider();
    const schemas = { signup: signupSchema, login: loginSchema };
    const methods = useForm({ resolver: yupResolver(schemas[step]) });
    const { handleSubmit } = methods;
    const onSubmit = useCallback(async (data) => {
        var _a, _b, _c, _d, _e;
        const url = step === "signup"
            ? router("auth.signup.ajax")
            : router("auth.login.ajax");
        const actions = JSON.stringify([{
                action: "add_reaction",
                details: { campaign_update_id: updateId, type: selectedReaction.toLowerCase() },
            }]);
        const formData = {
            email: data.email,
            password: data.password,
            actions,
        };
        if (step === "signup") {
            formData.name = data.name;
            formData.password_confirmation = data.password_confirmation;
        }
        else {
            formData.mfa = { code: data.mfaCode, type: "authenticator" };
        }
        const headers = {
            ...csrf(),
            ...captcha(captchaToken !== null && captchaToken !== void 0 ? captchaToken : "", ""),
        };
        try {
            const response = await fetch(request(url, "POST", headers, formData));
            if (response.redirected) {
                onSuccess(selectedReaction, null);
                return;
            }
            if (!response.ok) {
                const err = await response.json();
                throw new Error(((_a = err.error) === null || _a === void 0 ? void 0 : _a.message) || "An error occurred");
            }
            const json = await response.json();
            const responseData = json.data || json;
            if (responseData.success === true) {
                onSuccess(selectedReaction, (_b = responseData.csrf_token) !== null && _b !== void 0 ? _b : null);
            }
            else if (((_c = responseData.error) === null || _c === void 0 ? void 0 : _c.message) === "The account already exists") {
                onError("An account with this email already exists. Please log in instead.");
            }
            else {
                onError((_e = (_d = responseData.error) === null || _d === void 0 ? void 0 : _d.message) !== null && _e !== void 0 ? _e : "An error occurred");
            }
        }
        catch (err) {
            onError(err instanceof Error ? err.message : "An error occurred");
        }
    }, [step, updateId, selectedReaction, captchaToken, onSuccess, onError]);
    const requiresMfa = Boolean(errorMessage === null || errorMessage === void 0 ? void 0 : errorMessage.includes("Multifactor Authentication Code"));
    const FormComponent = step === "signup" ? SignupForm : LoginForm;
    return (_jsx(ModalLoginContainer, { type: step, modalTitle: step === "login" ? t("Login") : t("Sign up"), switchLoginOrSignupHandler: onSwitch, errorMessage: errorMessage !== null && errorMessage !== void 0 ? errorMessage : undefined, isError: Boolean(errorMessage), classPrefix: "reactions-modal", children: _jsx(FormProvider, { ...methods, children: _jsx(FormComponent, { onSubmit: handleSubmit(onSubmit), translate: t, isPosting: isPosting || isRefreshing, ...(step === "login" && { requiresMfa }), isRefreshing: isRefreshing }) }) }));
};
function ReactionsModal({ isOpen, onClose, updateId, selectedReaction, onReacted, }) {
    const { translate: t } = useLanguageProvider();
    const client = useApolloClient();
    const [step, setStep] = useState("login");
    const [isPosting, setIsPosting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(true);
    const turnstileRef = useRef(null);
    const handleClose = () => {
        onClose();
        setStep("login");
        setErrorMessage(null);
        setCaptchaToken(null);
        setIsRefreshing(true);
    };
    const handleSwitch = () => {
        var _a, _b;
        setStep((s) => (s === "login" ? "signup" : "login"));
        setErrorMessage(null);
        setCaptchaToken(null);
        setIsRefreshing(true);
        (_b = (_a = turnstileRef.current) === null || _a === void 0 ? void 0 : _a.reset) === null || _b === void 0 ? void 0 : _b.call(_a);
    };
    const handleSuccess = (reactionType, csrfToken) => {
        var _a;
        if (csrfToken) {
            (_a = document.head.querySelector("[name=\"csrf-token\"]")) === null || _a === void 0 ? void 0 : _a.setAttribute("content", csrfToken);
        }
        setIsPosting(false);
        if (step === "login") {
            onReacted(reactionType, true);
            client.refetchQueries({ include: "active" });
            handleClose();
        }
        else {
            setStep("signup_confirm");
        }
    };
    const handleError = (msg) => {
        var _a, _b;
        setErrorMessage(msg);
        setIsPosting(false);
        setCaptchaToken(null);
        setIsRefreshing(true);
        (_b = (_a = turnstileRef.current) === null || _a === void 0 ? void 0 : _a.reset) === null || _b === void 0 ? void 0 : _b.call(_a);
    };
    const reactionIcon = REACTION_ICONS.find((r) => r.id === selectedReaction);
    return (_jsx(LoginPromptModal, { isOpen: isOpen, onClose: handleClose, contentLabel: t("Sign up or log in to react"), children: step === "signup_confirm" ? (_jsxs("div", { className: "reactions-modal__content", children: [_jsx("div", { className: "reactions-modal__header", children: _jsx("div", { className: "reactions-modal__heading", children: t("Almost done!") }) }), _jsx("div", { className: "reactions-modal__body", children: _jsx("p", { children: t("Please go to your email account and click on the link in the email. Once you do this your reaction will be posted.") }) })] })) : (_jsxs(_Fragment, { children: [reactionIcon && (_jsxs("div", { className: "reactions-modal__selected-reaction", children: [_jsx("span", { className: "reactions-modal__selected-reaction-prompt", children: t("Authenticate to post your reaction") }), _jsx("span", { className: `reactions-modal__selected-reaction-icon --${selectedReaction.toLowerCase()}`, children: reactionIcon.icon })] })), _jsx(AuthForm, { step: step, updateId: updateId, selectedReaction: selectedReaction, captchaToken: captchaToken, isPosting: isPosting, isRefreshing: isRefreshing, errorMessage: errorMessage, onSwitch: handleSwitch, onSuccess: handleSuccess, onError: handleError }), _jsx("div", { className: "reactions-modal__turnstile", children: _jsx(TypedTurnstileWrapper, { ref: turnstileRef, emitNullOnReset: true, onChange: (token) => {
                            setCaptchaToken(token);
                            setIsRefreshing(false);
                        }, setTurnstileInteractive: () => setIsRefreshing(true), captchaKey: TURNSTILE_KEY, onError: () => setIsRefreshing(false), action: "reaction" }) })] })) }));
}
export default ReactionsModal;
